import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "components/Shared/Modal";
import { Camera } from "lucide-react"; // ícone da câmera

import * as S from "./styles";
import { useQuery, useQueryClient } from "react-query";
import { useCheckList } from "hooks/api/useCheckList";
import { useToast } from "components/ui/use-toast";

interface Props {
  links: string[];
  orderId: number;
  itens: any[];
}

interface IResponseFileRequest {
  link: string;
  extension: string;
  question_text?: string;
}

export const ModalChecklistFiles: React.FC<Props> = ({ links, orderId, itens }) => {
  const [isOpen, setIsopen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const { getCheckListLinks } = useCheckList();
  const { toast } = useToast();
  const queryClient = useQueryClient();


  const onClose = useCallback(() => {
    queryClient.removeQueries("getCheckListLinks");
    setActiveIndex(0);
    setIsopen(false);
  }, [queryClient]);

  const { data: files, isLoading, refetch } = useQuery({
    queryKey: ["getCheckListLinks", links],
    queryFn: () =>
      getCheckListLinks({
        links,
      }),
    enabled: false,
    onError: () => {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Erro ao carregar arquivos",
        duration: 5000,
      });
    },
  });

  const resolvedFiles = (files || []) as IResponseFileRequest[];

  useEffect(() => {
    if (isOpen && !files) {
      refetch();
    }
  }, [refetch, isOpen, files]);

  useEffect(() => {
    setActiveIndex(0);
  }, [links]);
  

  return (
    <>
      <Modal isOpen={isOpen}>
        <S.Container>
          <S.Header>
            <S.Title>Arquivos do Checklist - ID: {orderId}</S.Title>
          </S.Header>

          <div className="max-h-[60vh] p-2 flex flex-col">
            {isLoading && (
              <p className="text-gray-500 text-sm">Carregando arquivos...</p>
            )}

            {!isLoading && resolvedFiles.length > 0 && (
              <div className="flex flex-col space-y-2">
                {/* Abas */}
                <div className="flex space-x-2 overflow-x-auto pb-2">
                  {resolvedFiles.map((file: IResponseFileRequest, index: number) => (
                    <button
                      key={index}
                      onClick={() => setActiveIndex(index)}
                      className={`px-3 py-1 rounded-md text-sm font-medium border
              ${activeIndex === index ? "bg-gray-200" : "bg-gray-100"}
            `}
                    >
                      {`Arquivo ${index + 1}`}
                    </button>
                  ))}
                </div>
                {/* Texto da questão */}
            {itens?.[activeIndex]?.question_text && (
              <p className="text-center text-sm font-medium text-gray-700">
                {itens[activeIndex].question_text}
              </p>
            )}
                {/* Conteúdo ativo */}
                <div className="flex-1">
                  {(() => {
                    const file = resolvedFiles[activeIndex];
                    const isImage = [
                      "jpeg",
                      "jpg",
                      "png",
                      "gif",
                      "webp",
                    ].includes(file?.extension.toLowerCase());
                    const isPdf = file?.extension.toLowerCase() === "pdf";

                    if (isImage) {
                      return (
                        <img
                          src={file.link}
                          alt={`file-${activeIndex}`}
                          className="w-full h-[50vh] object-contain rounded-lg border"
                        />
                      );
                    }

                    if (isPdf) {
                      return (
                        <embed
                          src={file.link}
                          type="application/pdf"
                          title={`pdf-${activeIndex}`}
                          className="w-full h-[50vh] rounded-lg "
                        />
                      );
                    }

                    return (
                      <a
                        href={file.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        Abrir arquivo
                      </a>
                    );
                  })()}
                </div>
              </div>
            )}

            {!isLoading && resolvedFiles.length === 0 && (
              <p className="text-gray-500 text-sm">Nenhum arquivo encontrado para este checklist.</p>
            )}
          </div>
          <S.ButtonsWrapper>
            <S.Button btStyle="cancel" onClick={onClose} type="button">
              Fechar
            </S.Button>
          </S.ButtonsWrapper>
        </S.Container>
      </Modal>

      {/* Botão com ícone da câmera e contador */}
      <button
        onClick={() => setIsopen(true)}
        disabled={links.length === 0}
        className={`flex items-center gap-1 px-3 py-2 rounded-2xl shadow-sm transition
    ${
      links.length === 0
        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
        : "bg-gray-100 hover:bg-gray-200 text-gray-700"
    }`}
      >
        <Camera className="w-5 h-5" />
        <span className="text-sm font-medium">{links.length}</span>
      </button>
    </>
  );
};
